import React, { useState } from 'react';
import { FaTools, FaMoneyBillWave, FaClock, FaHardHat, FaFileAlt, FaUsers, FaCogs, FaExclamationTriangle, FaProjectDiagram, FaComments, FaChartLine, FaTruck, FaUserShield, FaHandshake } from 'react-icons/fa';

const issuesData = [
  { icon: <FaTools />, title: 'Technological Advancement', solution: 'We provide cutting-edge automation and smart tech integration.' },
  { icon: <FaFileAlt />, title: 'Managing Documentation', solution: 'Digitized document management systems streamline workflow.' },
  { icon: <FaMoneyBillWave />, title: 'Cash Flow Issue', solution: 'Optimized budgeting and flexible payment structures.' },
  { icon: <FaClock />, title: 'On-time Project Completion', solution: 'Agile methodology and real-time progress monitoring.' },
  { icon: <FaHardHat />, title: 'Insufficient Equipment', solution: 'Wide inventory and partnerships ensure timely supply.' },
  { icon: <FaComments />, title: 'Client Interaction', solution: 'Dedicated communication managers and digital channels.' },
  { icon: <FaCogs />, title: 'Compliance of Regulations', solution: 'Strict adherence with regulatory bodies and regular audits.' },
  { icon: <FaExclamationTriangle />, title: 'Construction Safety', solution: 'Regular training, safety gear, and protocol enforcement.' },
  { icon: <FaProjectDiagram />, title: 'Visualization', solution: '3D modeling and virtual project tours using BIM.' },
  { icon: <FaUsers />, title: 'Communication Problems', solution: 'Centralized communication hub and collaboration tools.' },
  { icon: <FaChartLine />, title: 'Lack of Skilled Workforce', solution: 'On-job training programs and expert hiring.' },
  { icon: <FaChartLine />, title: 'Insufficient Productivity', solution: 'Lean construction techniques and automation.' },
  { icon: <FaTruck />, title: 'Issue with Supply Chain', solution: 'Reliable vendors and dynamic logistics strategies.' },
  { icon: <FaUserShield />, title: 'Theft on Site / Security', solution: 'CCTV monitoring and strict access control.' },
  { icon: <FaHandshake />, title: 'Lack of Reliable Subcontractors', solution: 'Vetted subcontractor network and performance reviews.' },
];

const Issue = () => {
  const [filter, setFilter] = useState('All');

  const categories = ['All', 'Technology', 'Management', 'Operations'];

  const getCategory = (title) => {
    if (title.toLowerCase().includes('tech')) return 'Technology';
    if (title.toLowerCase().includes('documentation') || title.toLowerCase().includes('cash') || title.toLowerCase().includes('client') || title.toLowerCase().includes('communication')) return 'Management';
    return 'Operations';
  };

  const filteredIssues = filter === 'All' ? issuesData : issuesData.filter(issue => getCategory(issue.title) === filter);

  return (
    <div className="px-6 py-16 bg-gray-50 min-h-screen">
      <div 
        className="mx-auto"
        style={{
          maxWidth: '1440px', // max container width for general desktop
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          // Center container and limit max width on large desktops
        }}
      >
        <h1 className="text-center mb-6 font-bold text-gray-800" 
          style={{color:'deepskyblue', fontSize: '2.5rem', lineHeight: 1.2}}
        >
          Our Industry Challenges & Solutions
        </h1>
        <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto" style={{fontSize: '1.125rem', lineHeight: 1.6}}>
          The construction industry is expanding beyond its conventional business scope in step with the changing market trends and future. In response to the growing demands for a ‘All Solutions Under One Roof’ beyond EPC, Labh Industries is enhancing its competitiveness by combining our excellent project execution capabilities accumulated from many large-scale global projects with the extensive network of Group companies. We will continue to meet the needs of our customers as the leading reliable solution provider.
        </p>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2 rounded-full font-medium transition-all duration-300 
                ${filter === cat ? 'bg-yellow-500 text-white' : 'bg-white text-yellow-500 border border-yellow-500 hover:bg-yellow-100'}`}
              style={{ minWidth: '100px', fontSize: '1rem' }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Issues Grid */}
        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            // auto-fit with minmax allows grid to automatically adjust from 1 column on mobile up to many columns on wide screens
          }}
        >
          {filteredIssues.map((issue, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition duration-300"
              style={{ minHeight: '220px', display: 'flex', flexDirection: 'column' }}
            >
              <div className="text-yellow-500 text-4xl mb-4" style={{flexShrink: 0}}>{issue.icon}</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2" style={{flexShrink: 0}}>{issue.title}</h3>
              <p className="text-gray-600 text-sm" style={{flexGrow: 1}}>{issue.solution}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Ultra-wide screen styling */}
      <style>
        {`
          @media screen and (min-width: 2560px) {
            div.mx-auto {
              max-width: 1800px !important; /* allow more width on ultra-wide screens */
            }
            h1 {
              font-size: 3.5rem !important;
            }
            p {
              font-size: 1.25rem !important;
              max-width: 60ch !important;
            }
            button {
              font-size: 1.125rem !important;
              min-width: 130px !important;
              padding: 0.75rem 1.5rem !important;
            }
            div.grid {
              gap: 2rem !important;
              grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)) !important;
            }
            div.grid > div {
              min-height: 250px !important;
              padding: 1.5rem !important;
            }
          }
          @media screen and (min-width: 3840px) {
            div.mx-auto {
              max-width: 2200px !important;
              padding-left: 3rem !important;
              padding-right: 3rem !important;
            }
            h1 {
              font-size: 4rem !important;
            }
            p {
              font-size: 1.375rem !important;
              max-width: 70ch !important;
            }
            button {
              font-size: 1.25rem !important;
              min-width: 160px !important;
              padding: 1rem 2rem !important;
            }
            div.grid {
              gap: 2.5rem !important;
              grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)) !important;
            }
            div.grid > div {
              min-height: 280px !important;
              padding: 2rem !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Issue;
